import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { Friend } from '@omokage/shared';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PlayerTag } from './PlayerTag';
import { PlayerSelectDialog } from './PlayerSelectDialog';
import { useRemovePlayerFromPhoto } from '@/hooks/usePhotoFriends';

/**
 * PlayersSection - Manage players in a photo
 *
 * Features:
 * - Display player tags with remove button
 * - Add players via dialog
 * - Confirm before deletion
 * - Loading states
 * - Empty state
 *
 * Usage:
 * ```tsx
 * <PlayersSection photoId="photo123" players={photo.friends} />
 * ```
 */
export interface PlayersSectionProps {
  /** Photo ID */
  photoId: string;
  /** Current players in photo */
  players: Friend[];
  /** Callback when player is removed */
  onPlayerRemoved?: (vrchatId: string) => void;
  /** Callback when player is added */
  onPlayerAdded?: (player: Friend) => void;
  /** Read-only mode (hides add/remove buttons) */
  readOnly?: boolean;
}

export function PlayersSection({
  photoId,
  players,
  onPlayerRemoved,
  onPlayerAdded,
  readOnly = false,
}: PlayersSectionProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [playerToRemove, setPlayerToRemove] = useState<Friend | null>(null);
  const removePlayer = useRemovePlayerFromPhoto();

  const handleRemoveClick = (vrchatId: string) => {
    const player = players.find((p) => p.vrchatId === vrchatId);
    if (player) {
      setPlayerToRemove(player);
    }
  };

  const handleConfirmRemove = async () => {
    if (!playerToRemove) return;

    try {
      await removePlayer.mutateAsync({
        photoId,
        vrchatId: playerToRemove.vrchatId,
      });
      onPlayerRemoved?.(playerToRemove.vrchatId);
      setPlayerToRemove(null);
    } catch (error) {
      console.error('Failed to remove player:', error);
    }
  };

  const handleCancelRemove = () => {
    setPlayerToRemove(null);
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Players {players.length > 0 && `(${players.length})`}
        </h4>
        {!readOnly && (
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <Plus className="w-3 h-3" />
            Add
          </Button>
        )}
      </div>

      {/* Player List */}
      {players.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded">
          No players added yet
        </div>
      ) : (
        <div className="space-y-2">
          {players.map((player) => (
            <PlayerTag
              key={player.vrchatId}
              player={player}
              onRemove={readOnly ? undefined : handleRemoveClick}
              disabled={removePlayer.isPending}
            />
          ))}
        </div>
      )}

      {/* Add Player Dialog */}
      <PlayerSelectDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        photoId={photoId}
        existingPlayers={players}
        onPlayerAdded={onPlayerAdded}
      />

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!playerToRemove} onOpenChange={(open) => !open && handleCancelRemove()}>
        <AlertDialogContent className="z-[70] bg-card border-border text-card-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Player</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to remove{' '}
              <span className="font-medium text-foreground">{playerToRemove?.displayName}</span>{' '}
              from this photo?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancelRemove}
              className="bg-muted border-border text-foreground hover:bg-muted/80"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={removePlayer.isPending}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {removePlayer.isPending ? (
                <>
                  <Trash2 className="w-4 h-4 mr-2 animate-pulse" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
